/**
 * HPQ Customer Account API helper.
 *
 * Token storage: the access/id tokens (TOKENS_KEY) and the refresh_token
 * (REFRESH_TOKEN_KEY) live in localStorage so the session survives reloads and
 * can be silently refreshed; PKCE verifier/state live there briefly across the
 * OAuth redirect. This is the standard public-client (SPA) model on a
 * Standard-Shopify theme, where HTTP-only cookie / web-worker token isolation
 * is not available. THREAT MODEL: a script that runs on this origin (theme or
 * installed app) can read these tokens; the refresh_token is the more sensitive
 * one (a longer-lived bearer). Mitigation rests on keeping third-party scripts
 * off the storefront origin and on the storefront CSP — not on storage secrecy.
 */
(function () {
  'use strict';

  var DISCOVERY_TTL_MS = 60 * 60 * 1000;
  var PKCE_TTL_MS = 15 * 60 * 1000;
  var DISCOVERY_PREFIX = 'hpq_caa_discovery_';
  var PKCE_STATE_KEY = 'hpq_caa_pkce_state';
  // The refresh_token lives in its OWN key, separate from TOKENS_KEY. getCachedTokens
  // clears TOKENS_KEY when the id_token expires; keeping the refresh_token apart means
  // an expired session still has the material to silently refresh (no re-login).
  var REFRESH_TOKEN_KEY = 'hpq_caa_refresh_token';
  var TOKENS_KEY = 'hpq_caa_tokens';
  // Session validity is keyed on the ACCESS token, via an absolute expiresAt we
  // compute from the token response's expires_in (see normalizeTokens). The
  // id_token is NOT authoritative for validity: Shopify's refresh grant does not
  // issue id_tokens at all (verified against production 2026-08-19 — the refresh
  // response is exactly access_token/refresh_token/token_type/expires_in), so an
  // id_token-keyed check dooms every silent refresh. Treat a session as expired
  // this long BEFORE its stated expiry so a token can't die mid-request.
  var EXPIRY_SKEW_MS = 30 * 1000;
  // Name given to the login popup. It survives the popup's cross-origin
  // round-trip to Shopify and back, so the callback page can reliably identify
  // itself as our popup (vs. a full-page redirect return that merely has a
  // window.opener). MUST match the literal used in hpq-silent-auth-init.js and
  // the fortune-footer callback guard.
  var POPUP_WINDOW_NAME = 'hpq_caa_login';
  var POPUP_WIDTH = 480;
  var POPUP_HEIGHT = 720;
  // Shared-localStorage marker the opener sets while a login popup is active.
  // Because it lives in same-origin localStorage it survives the popup's
  // cross-origin round-trip to Shopify (which cannot touch our storage) and is
  // readable by the popup's callback page — a robust way to tell our login
  // popup apart from an ordinary full-page redirect return. MUST match the
  // literal in hpq-silent-auth-init.js.
  var POPUP_ACTIVE_KEY = 'hpq_caa_popup_active';
  var pkceInFlight = false;
  // The OAuth error codes that genuinely mean "the server has rejected
  // this refresh token and will keep rejecting it until the shopper
  // re-authenticates." Per RFC 6749 §5.2 and Shopify's CAA token
  // endpoint. Anything else from the token endpoint — an empty body,
  // an HTML body, an abnormal 4xx without a parseable error, a 502 from
  // an edge proxy — is treated as transient: the refresh token is
  // kept for a later attempt. Mass logout on a few minutes of
  // token-endpoint misbehaviour is the exact symptom this guard
  // prevents.
  var TERMINAL_OAUTH_ERRORS = {
    invalid_grant: true,
    invalid_client: true,
    unauthorized_client: true
  };

  var cachedTokens = null;
  var cachedCustomer = null;
  var pkceCleanupTimer = null;

  function normalizeShopDomain(shopDomain) {
    var domain = String(shopDomain || '').trim();
    domain = domain.replace(/^https?:\/\//i, '');
    domain = domain.split('/')[0];
    return domain.replace(/\/+$/, '');
  }

  function storageGet(storage, key) {
    try { return storage.getItem(key); } catch (e) { return null; }
  }

  function storageSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (e) { /* noop */ }
  }

  function storageRemove(storage, key) {
    try { storage.removeItem(key); } catch (e) { /* noop */ }
  }

  function parseJSON(value) {
    if (!value) return null;
    try { return JSON.parse(value); } catch (e) { return null; }
  }

  function base64UrlFromBytes(bytes) {
    var binary = '';
    for (var i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  function randomBase64Url(byteLength) {
    if (!window.crypto || !window.crypto.getRandomValues) {
      throw new Error('crypto_unavailable');
    }
    var bytes = new Uint8Array(byteLength);
    window.crypto.getRandomValues(bytes);
    return base64UrlFromBytes(bytes);
  }

  function generateIdempotencyNonce() {
    return randomBase64Url(32);
  }

  // Session validity keyed on the ACCESS token. The id_token is deliberately
  // ignored here: Shopify's refresh grant returns no id_token, and the Customer
  // Account API authorizes on the access_token alone (verified live 2026-08-19 —
  // a refreshed access token with no id_token present anywhere fetches the
  // customer fine). Legacy tokens persisted before this build carry no
  // expiresAt; they are treated as expired so they route through the silent
  // refresh path (which now works) rather than being trusted indefinitely on a
  // stored shape we can no longer validate.
  function accessTokenUsable(tokens) {
    if (!tokens || !tokens.accessToken) return false;
    if (typeof tokens.expiresAt !== 'number') return false;
    return Date.now() < tokens.expiresAt - EXPIRY_SKEW_MS;
  }

  // Check the persisted blob directly, without mutating the in-memory cache.
  // hpq-flyout-auth-state.js uses this to reconcile the drawer with the real
  // session — getCachedTokens() would DELETE an expired blob (correct for its
  // own callers, but the drawer must not destroy the record of a refreshable
  // session; the refresh path needs the shape preserved to route correctly).
  function hasUsableSession() {
    if (cachedTokens && accessTokenUsable(cachedTokens)) return true;
    return accessTokenUsable(parseJSON(storageGet(window.localStorage, TOKENS_KEY)));
  }

  // Non-destructive read used by the refresh-grant terminal branch to
  // decide whether another tab has already minted a usable session. We
  // explicitly MUST NOT call getCachedTokens() here: it DELETES an
  // expired blob from storage and short-circuits on stale in-memory
  // state — exactly the destructive read that wiped the winner's tokens
  // in the two-tab rotation race. This helper reads STORAGE FIRST
  // (the source of truth across tabs; storage events only flow between
  // tabs, not within the writing tab), then falls back to the cache for
  // the case where this tab was the writer. Mutates neither.
  function readAdoptableTokens() {
    var stored = parseJSON(storageGet(window.localStorage, TOKENS_KEY));
    if (stored && accessTokenUsable(stored)) return stored;
    if (cachedTokens && accessTokenUsable(cachedTokens)) return cachedTokens;
    return null;
  }

  function requireCryptoSubtle() {
    if (!window.crypto || !window.crypto.subtle || !window.TextEncoder) {
      throw new Error('pkce_crypto_unavailable');
    }
  }

  function sha256Base64Url(value) {
    requireCryptoSubtle();
    var bytes = new TextEncoder().encode(value);
    return window.crypto.subtle.digest('SHA-256', bytes).then(function (digest) {
      return base64UrlFromBytes(new Uint8Array(digest));
    });
  }

  function discoveryCacheKey(shopDomain) {
    return DISCOVERY_PREFIX + normalizeShopDomain(shopDomain);
  }

  function validDiscovery(value) {
    return !!(value &&
      value.authorization_endpoint &&
      value.token_endpoint &&
      value.graphql_api);
  }

  function fetchJSON(url) {
    return window.fetch(url, { credentials: 'omit' }).then(function (res) {
      return res.text().then(function (rawText) {
        var parsed = parseJSON(rawText);
        if (!res.ok || !parsed) {
          throw new Error('discovery_failed');
        }
        return parsed;
      });
    });
  }

  function discoverEndpoints(shopDomain) {
    var domain = normalizeShopDomain(shopDomain);
    if (!domain) return Promise.reject(new Error('missing_shop_domain'));

    var cacheKey = discoveryCacheKey(domain);
    var cached = parseJSON(storageGet(window.sessionStorage, cacheKey));
    if (cached &&
        cached.created_at &&
        Date.now() - cached.created_at < DISCOVERY_TTL_MS &&
        validDiscovery(cached.discovery)) {
      return Promise.resolve(cached.discovery);
    }

    var origin = 'https://' + domain;
    return Promise.all([
      fetchJSON(origin + '/.well-known/openid-configuration'),
      fetchJSON(origin + '/.well-known/customer-account-api')
    ]).then(function (parts) {
      var authConfig = parts[0];
      var apiConfig = parts[1];
      var discovery = {
        authorization_endpoint: authConfig.authorization_endpoint,
        token_endpoint: authConfig.token_endpoint,
        end_session_endpoint: authConfig.end_session_endpoint || null,
        jwks_uri: authConfig.jwks_uri || null,
        graphql_api: apiConfig.graphql_api,
        mcp_api: apiConfig.mcp_api || null
      };

      if (!validDiscovery(discovery)) {
        throw new Error('invalid_discovery');
      }

      storageSet(window.sessionStorage, cacheKey, JSON.stringify({
        created_at: Date.now(),
        discovery: discovery
      }));
      return discovery;
    });
  }

  // key defaults to the single PKCE state key; the optional param is retained
  // for callers that may namespace their own PKCE slot.
  function readStoredPkceState(key) {
    key = key || PKCE_STATE_KEY;
    var stored = parseJSON(storageGet(window.localStorage, key));
    if (!stored || !stored.state || !stored.code_verifier || !stored.created_at) {
      return null;
    }
    if (Date.now() - stored.created_at > PKCE_TTL_MS) {
      storageRemove(window.localStorage, key);
      return null;
    }
    return stored;
  }

  function clearStoredPkceState(key) {
    storageRemove(window.localStorage, key || PKCE_STATE_KEY);
    if (pkceCleanupTimer) {
      clearTimeout(pkceCleanupTimer);
      pkceCleanupTimer = null;
    }
  }

  function schedulePkceCleanup(expectedState, key) {
    key = key || PKCE_STATE_KEY;
    if (pkceCleanupTimer) clearTimeout(pkceCleanupTimer);
    pkceCleanupTimer = setTimeout(function () {
      var stored = readStoredPkceState(key);
      if (stored && stored.state === expectedState) {
        clearStoredPkceState(key);
      }
    }, PKCE_TTL_MS);
  }

  function buildAuthorizationUrl(discovery, opts, codeChallenge, state) {
    var url = new URL(discovery.authorization_endpoint);
    url.searchParams.set('scope', opts.scope);
    url.searchParams.set('client_id', opts.clientId);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('redirect_uri', opts.redirectUri);
    url.searchParams.set('state', state);
    url.searchParams.set('nonce', randomBase64Url(32));
    url.searchParams.set('code_challenge', codeChallenge);
    url.searchParams.set('code_challenge_method', 'S256');
    return url.toString();
  }

  function startPkceFlow(opts) {
    if (pkceInFlight) {
      console.warn('[CAA] startPkceFlow: another flow already in progress; ignoring duplicate start.');
      return;
    }
    pkceInFlight = true;
    try {
      var options = opts || {};
      if (!options.shopDomain) throw new Error('missing_shop_domain');
      if (!options.clientId) throw new Error('missing_client_id');
      if (!options.scope) throw new Error('missing_scope');
      if (!options.redirectUri) throw new Error('missing_redirect_uri');
      requireCryptoSubtle();

      var codeVerifier = randomBase64Url(64);
      var state = randomBase64Url(32);
      var stored = {
        state: state,
        code_verifier: codeVerifier,
        created_at: Date.now()
      };

      storageSet(window.localStorage, PKCE_STATE_KEY, JSON.stringify(stored));
      schedulePkceCleanup(state);

      Promise.all([
        discoverEndpoints(options.shopDomain),
        sha256Base64Url(codeVerifier)
      ]).then(function (parts) {
        if (!pkceInFlight) return;
        window.location.href = buildAuthorizationUrl(parts[0], options, parts[1], state);
      }).catch(function (err) {
        clearStoredPkceState();
        pkceInFlight = false;
        throw err;
      });
    } catch (e) {
      pkceInFlight = false;
      throw e;
    }
  }

  function tokenRequestBody(args, codeVerifier) {
    var body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', args.clientId);
    body.set('redirect_uri', args.redirectUri);
    body.set('code', args.code);
    body.set('code_verifier', codeVerifier);
    return body;
  }

  // id_token is OPTIONAL: the initial authorization-code grant returns one, but
  // the refresh grant does not (see the comment at the top of this file). It is
  // kept when present for identity claims and the logout id_token_hint, but it
  // is no longer required to form a session.
  function normalizeTokens(raw) {
    if (!raw || !raw.access_token) {
      throw new Error('invalid_token_response');
    }
    // expires_in arrives as a number in practice but the token endpoint makes no
    // type guarantee — a JSON string "3600" would otherwise leave expiresAt null
    // and silently reinstate the refresh-every-page-load failure. Coerce and
    // require a positive finite number. A missing/invalid value yields null,
    // which accessTokenUsable treats as expired: the session routes through the
    // silent refresh path (fail-SAFE — never trust a session we can't age) at
    // the cost of one extra refresh on the next load.
    var expiresInSec = Number(raw.expires_in);
    var hasValidExpiry = isFinite(expiresInSec) && expiresInSec > 0;
    return {
      accessToken: raw.access_token,
      idToken: raw.id_token || null,
      expiresIn: hasValidExpiry ? expiresInSec : null,
      // Absolute expiry, computed once at storage time. The refresh grant's
      // response carries expires_in but no parseable id_token, so this is the
      // only expiry signal available on every path.
      expiresAt: hasValidExpiry ? Date.now() + expiresInSec * 1000 : null,
      tokenType: raw.token_type || 'Bearer'
    };
  }

  function handlePkceCallback(args) {
    var options = args || {};
    var stateKey = options.stateKey; // optional PKCE-slot namespace; default when omitted
    return Promise.resolve().then(function () {
      if (!options.code || !options.state) throw new Error('missing_callback_params');
      if (!options.shopDomain) throw new Error('missing_shop_domain');
      if (!options.clientId) throw new Error('missing_client_id');
      if (!options.redirectUri) throw new Error('missing_redirect_uri');

      var stored = readStoredPkceState(stateKey);
      if (!stored || stored.state !== options.state) {
        throw new Error('state_mismatch');
      }

      return discoverEndpoints(options.shopDomain).then(function (discovery) {
        return window.fetch(discovery.token_endpoint, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: tokenRequestBody(options, stored.code_verifier)
        }).then(function (res) {
          return res.text().then(function (rawText) {
            var parsed = parseJSON(rawText);
            if (!res.ok || !parsed) {
              throw new Error('token_exchange_failed');
            }
            cachedTokens = normalizeTokens(parsed);
            storageSet(window.localStorage, TOKENS_KEY, JSON.stringify(cachedTokens));
            persistRefreshToken(parsed);
            cachedCustomer = null;
            emitAuthStateChanged('caa_token_set');
            return cachedTokens;
          });
        });
      });
    }).then(function (tokens) {
      clearStoredPkceState(stateKey);
      pkceInFlight = false;
      return tokens;
    }).catch(function (err) {
      clearStoredPkceState(stateKey);
      pkceInFlight = false;
      throw err;
    });
  }

  function handlePkceCallbackError(args) {
    clearStoredPkceState();
    pkceInFlight = false;
  }

  function normalizeCustomer(customer) {
    if (!customer) return null;
    var emailNode = customer.emailAddress || {};
    var phoneNode = customer.phoneNumber || null;
    return {
      gid: customer.id || null,
      email: emailNode.emailAddress || null,
      firstName: customer.firstName || null,
      phone: (phoneNode && phoneNode.phoneNumber) || null,
      smsConsentState: (phoneNode && phoneNode.marketingState) || null
    };
  }

  function getCustomerFromTokens(idToken, accessToken, shopDomain) {
    // Access token is the credential; id_token is optional metadata. Shopify's
    // refresh grant returns no id_token, and the Customer Account API authorizes
    // on the access_token alone — so an absent or stale id_token must not
    // reject, and must NEVER trigger clearTokens() (which would destroy the
    // refresh token and log the shopper out permanently). A genuine auth
    // rejection surfaces as a fetch error from the API itself.
    if (!accessToken) return Promise.reject(new Error('missing_tokens'));
    return discoverEndpoints(shopDomain).then(function (discovery) {
      return window.fetch(discovery.graphql_api, {
        method: 'POST',
        headers: {
          'Authorization': accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: '{ customer { id emailAddress { emailAddress } firstName phoneNumber { phoneNumber marketingState } } }'
        })
      });
    }).then(function (res) {
      return res.text().then(function (rawText) {
        var parsed = parseJSON(rawText);
        if (!res.ok || !parsed || parsed.errors) {
          throw new Error('customer_fetch_failed');
        }
        var customer = normalizeCustomer(parsed.data && parsed.data.customer);
        if (!customer || !customer.gid) {
          throw new Error('customer_missing');
        }
        cachedCustomer = customer;
        // The token-set event fired earlier with cachedCustomer=null (customer loads async here).
        // Emit again now that the identity is known so customer-scoped consumers (e.g. the cart
        // coupon module re-pricing a Fortune reward) re-run with a real customer gid instead of
        // treating the shopper as anonymous.
        emitAuthStateChanged('customer_loaded');
        return customer;
      });
    });
  }

  function getCachedTokens() {
    if (!cachedTokens) {
      var stored = parseJSON(storageGet(window.localStorage, TOKENS_KEY));
      if (stored && stored.accessToken) {
        cachedTokens = stored;
      }
    }
    if (!cachedTokens) return null;
    if (!accessTokenUsable(cachedTokens)) {
      // Access token expired (or a legacy stored blob with no expiresAt — see
      // accessTokenUsable). Drop it, but KEEP the refresh_token — an expired
      // access token is exactly when the refresh_token is needed to silently
      // mint a new one (see refreshAccessToken). A full clearTokens() only
      // happens on explicit logout or a refresh that the server rejects. Also
      // drop the cached customer so getCachedCustomer() can't return a stale
      // identity while the session is unauthenticated.
      cachedTokens = null;
      cachedCustomer = null;
      storageRemove(window.localStorage, TOKENS_KEY);
      return null;
    }
    return cachedTokens;
  }

  function getCachedCustomer() {
    return cachedCustomer;
  }

  function clearTokens() {
    cachedTokens = null;
    cachedCustomer = null;
    storageRemove(window.localStorage, TOKENS_KEY);
    storageRemove(window.localStorage, REFRESH_TOKEN_KEY);
    emitAuthStateChanged('caa_token_clear');
  }

  // Clear the ACTIVE session (access/id token + cached customer) but KEEP the
  // refresh_token. Used by transient, non-auth failure paths (a customer-fetch
  // blip, a recognize bounce) so they don't cost the shopper a session that is
  // still silently refreshable. Full logout and a server-REJECTED refresh use
  // clearTokens() instead, which also drops the refresh token.
  function clearSession() {
    cachedTokens = null;
    cachedCustomer = null;
    storageRemove(window.localStorage, TOKENS_KEY);
    emitAuthStateChanged('caa_session_clear');
  }

  // Persist the refresh_token from a token response into its own key. If the
  // response omits one (Shopify may not rotate it on refresh), keep whatever is
  // already stored rather than dropping it.
  function persistRefreshToken(raw) {
    if (raw && raw.refresh_token) {
      storageSet(window.localStorage, REFRESH_TOKEN_KEY, raw.refresh_token);
    }
  }

  function getStoredRefreshToken() {
    return storageGet(window.localStorage, REFRESH_TOKEN_KEY) || null;
  }

  // Silent access-token refresh via the OAuth refresh_token grant — a plain POST
  // to the token endpoint, no iframe and no navigation, so it is unaffected by
  // Shopify's frame-ancestors 'none' CSP that makes hidden-iframe silent-auth
  // impossible on this store. Returns { ok:true, tokens } on success, or
  // { ok:false, reason } for 'no_refresh_token' (nothing stored — caller must
  // do interactive login), 'refresh_failed' (server rejected — tokens cleared),
  // or a transient error (tokens/refresh material preserved for a later retry).
  var refreshInFlight = null;

  // Coalesce concurrent refreshes: if a refresh is already running (e.g. the
  // page-load bridge and an apiFetch 401 both fire), a second caller shares the
  // same result instead of POSTing a second grant with the same refresh_token —
  // which, if Shopify rotates refresh tokens, would invalidate the first and log
  // the customer out.
  function refreshAccessToken(opts) {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = performTokenRefresh(opts).then(function (r) {
      refreshInFlight = null;
      return r;
    });
    return refreshInFlight;
  }

  function performTokenRefresh(opts) {
    var options = opts || {};
    return Promise.resolve().then(function () {
      if (!options.shopDomain) throw new Error('missing_shop_domain');
      if (!options.clientId) throw new Error('missing_client_id');
      var refreshToken = getStoredRefreshToken();
      if (!refreshToken) return { ok: false, reason: 'no_refresh_token' };

      return discoverEndpoints(options.shopDomain).then(function (discovery) {
        var body = new URLSearchParams();
        body.set('grant_type', 'refresh_token');
        body.set('client_id', options.clientId);
        body.set('refresh_token', refreshToken);
        return window.fetch(discovery.token_endpoint, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body
        }).then(function (res) {
          return res.text().then(function (rawText) {
            var parsed = parseJSON(rawText);

            // Success: a 2xx carrying an access_token. Shopify OMITS id_token
            // on the refresh grant (verified against production 2026-08-19: the
            // response is exactly access_token/refresh_token/token_type/
            // expires_in, and adding openid scopes to the POST does not change
            // that) — and the session doesn't need one. The id_token is kept
            // only as identity metadata (claims, logout hint), so when the
            // response carries no fresh one we retain the one we already hold
            // rather than failing.
            if (res.ok && parsed && parsed.access_token) {
              var priorIdToken = (cachedTokens && cachedTokens.idToken) ||
                (parseJSON(storageGet(window.localStorage, TOKENS_KEY)) || {}).idToken || null;
              var idToken = parsed.id_token || priorIdToken;
              cachedTokens = normalizeTokens({
                access_token: parsed.access_token,
                id_token: idToken,
                expires_in: parsed.expires_in,
                token_type: parsed.token_type
              });
              storageSet(window.localStorage, TOKENS_KEY, JSON.stringify(cachedTokens));
              persistRefreshToken(parsed);
              cachedCustomer = null;
              emitAuthStateChanged('caa_token_refresh');
              // A refresh restores the SESSION; it must also restore the IDENTITY. The line
              // above just dropped cachedCustomer, and on an already-booted page nothing else
              // refills it: fortune-footer's init() ran at DOMContentLoaded, saw expired
              // tokens, and skipped its customer fetch, while silent-auth's reconcile() only
              // toggles CSS classes. getCachedCustomer() would stay null for the rest of the
              // visit, so hpq-cart-coupon.js would send no customerId and Shopify would
              // silently decline every customer-scoped Fortune reward — which the shopper
              // reads as "that code can't be applied".
              //
              // Fire-and-forget on purpose: callers await tokens, not identity. The fetch
              // emits 'customer_loaded', and the cart-coupon module already re-quotes on
              // that event. A failure here leaves the shopper anonymous, exactly as before.
              //
               // Guard on a usable ACCESS token. An earlier version guarded on a fresh
               // id_token because getCustomerFromTokens() answered an expired id_token
               // with clearTokens() — destroying the refresh token along with the session.
               // That is no longer true: the id_token is optional metadata now, and a
               // stale or absent one never clears anything, so the only question that
               // matters here is whether the refresh actually produced a usable session.
               var refreshed = cachedTokens;
               if (accessTokenUsable(refreshed)) {
                 getCustomerFromTokens(refreshed.idToken, refreshed.accessToken, options.shopDomain)
                   .catch(function (err) {
                     // Fire-and-forget above means callers never see this — without a
                     // console signal a failing CAA GraphQL silently reintroduces
                     // anonymous pricing for a signed-in shopper. Log the reason only:
                     // NEVER the token value and NEVER the customer's email.
                     try {
                       console.warn('[CAA] identity refetch after refresh failed:', (err && err.message) || 'unknown');
                     } catch (e) { /* noop */ }
                   });
               }
              return { ok: true, tokens: cachedTokens };
            }

            // Transient server failure (rate-limit / 5xx): keep tokens + refresh
            // material for a later attempt — do NOT destroy the session.
            if (res.status === 429 || res.status >= 500) {
              return { ok: false, reason: 'refresh_transient' };
            }

            // Terminal rejection vs. parse/edge failure. The
            // DOCUMENTED-to-be-terminal OAuth errors are the
            // invalid_grant family — `invalid_grant`, `invalid_client`,
            // `unauthorized_client` — carried in a SUCCESSFULLY PARSED
            // body. Anything else (an empty body, an HTML body, an
            // abnormal 4xx, a Cloudflare challenge page, an unparseable
            // 502) is treated as transient: clearTokens() here would
            // log out every returning shopper on a few minutes of
            // token-endpoint misbehaviour, which is the exact mass-
            // logout bug B3 names. Adopting the winner via a non-
            // destructive read is the B4 half of the same fix.
            if (parsed && parsed.error && TERMINAL_OAUTH_ERRORS[parsed.error]) {
              var live = readAdoptableTokens();
              if (live) return { ok: true, tokens: live };
              clearTokens();
              return { ok: false, reason: 'refresh_failed' };
            }

            // Unparseable or unrecognized response: keep the refresh
            // token for a later attempt. This is the transient half of
            // B3 — absence of a parsed invalid_grant is NOT evidence
            // of rejection.
            return { ok: false, reason: 'refresh_transient' };
          });
        });
      });
    }).catch(function (err) {
      // Transient failure (network throw, discovery error, an unhandled
      // inner throw from a parsed non-terminal response): keep tokens +
      // refresh material so a later attempt can succeed. Normalise the
      // reason to a fixed 'refresh_transient' so callers have a single
      // transient string to branch on — err.message for a real network
      // failure is 'Failed to fetch', and for discovery problems is
      // 'discovery_failed' / 'invalid_discovery'. Each of those would
      // otherwise propagate as a fresh, never-before-seen reason that
      // footer's whitelist could only catch by accident. Belt-and-braces
      // alongside footer's default-transient mapping; do not leak the
      // underlying err.message into the response either way.
      try {
        if (err && err.message) console.warn('[CAA] silent refresh transient:', err.message);
      } catch (e) { /* noop */ }
      return { ok: false, reason: 'refresh_transient' };
    });
  }

  // Canonical PKCE redirect URI — always root path. This MUST match exactly
  // what's registered in the Shopify CAA Headless app's Callback URI(s).
  // Path-bearing variants would require every product URL to be allowlisted.
  // Pre-redirect callers should capture window.location.pathname + search in
  // sessionStorage under the key 'hpq:pkce-return-path' so they can restore
  // it after the callback. See handlePkceCallback for the restoration hook.
  function buildCanonicalRedirectUri() {
    return window.location.origin + '/?fortune_oauth_callback=1';
  }

  // Redirect URI for the popup interactive-login flow. Reuses the SAME URI as
  // the full-page redirect flow (fortune_oauth_callback=1) because that URI is
  // the one actually registered in the Shopify Headless CAA Callback allowlist.
  // The popup callback page is told apart from a full-page redirect return by
  // its window.name (POPUP_WINDOW_NAME, set when we open it and preserved across
  // the cross-origin round-trip) — see hpq-silent-auth-init.js relay and the
  // fortune-footer callback guard — and the popup posts a distinct message type
  // so it never cross-talks with the hidden-iframe silent flow.
  function buildPopupRedirectUri() {
    return window.location.origin + '/?fortune_oauth_callback=1';
  }

  // Whether the interactive popup-login flow is a good fit for this device.
  // Popups are a solid desktop experience but hostile on touch-primary devices
  // (phones/tablets open them as background tabs or block them, and the opener
  // relationship is unreliable), so those fall back to the full-page redirect.
  // '(pointer: fine)' is true whenever a precise pointer (mouse/trackpad) is
  // available — including hybrid touch laptops — and false on touch-only phones.
  function isPopupLoginSupported() {
    try {
      if (typeof window.open !== 'function') return false;
      if (!window.matchMedia) return false;
      return window.matchMedia('(pointer: fine)').matches;
    } catch (e) {
      return false;
    }
  }

  // Notify same-tab listeners (flyout reconciler, future consumers) that the
  // CAA token state changed. localStorage `storage` events only fire across
  // tabs, so we dispatch a synchronous CustomEvent for the writing tab.
  function emitAuthStateChanged(source) {
    try {
      document.dispatchEvent(new CustomEvent('hpq:auth-state-changed', {
        detail: { source: source || 'unknown' }
      }));
    } catch (e) { /* CustomEvent unavailable */ }
  }

  // CAA single-click logout via OIDC end_session_endpoint.
  // Including id_token_hint skips the Shopify confirmation interstitial
  // that /customer_identity/logout shows by default. Reference:
  // shopify.dev/docs/api/customer/latest under "Logging out".
  //
  // post_logout_redirect_uri is OPTIONAL — when omitted, Shopify redirects
  // to one of the URIs in the Headless CAA "Logout URI" setting. Pass it
  // explicitly only when you need to control the landing path AND have
  // confirmed the exact URI is allowlisted.
  function logout(opts) {
    return Promise.resolve().then(function () {
      var options = opts || {};
      if (!options.shopDomain) throw new Error('missing_shop_domain');

      // Read tokens DIRECTLY from localStorage rather than via getCachedTokens
      // — that helper filters out expired tokens, but for a logout hint an
      // expired id_token still uniquely identifies the session Shopify needs
      // to terminate. We'd rather send a stale hint than no hint at all.
      var rawTokens = parseJSON(storageGet(window.localStorage, TOKENS_KEY));
      var idTokenHint = rawTokens && rawTokens.idToken ? rawTokens.idToken : null;

      // Clear local tokens FIRST — user-intent honored even if remote fails.
      // Stale id_token_hint already captured above; safe to clear now.
      clearTokens();

      return discoverEndpoints(options.shopDomain).then(function (discovery) {
        if (!discovery.end_session_endpoint) {
          throw new Error('end_session_endpoint_missing');
        }

        var url = new URL(discovery.end_session_endpoint);
        if (idTokenHint) {
          url.searchParams.set('id_token_hint', idTokenHint);
          // post_logout_redirect_uri REQUIRES id_token_hint per Shopify spec —
          // sending it solo yields "Invalid id_token" (observed empirically
          // W26b 2026-05-15). Only include it when we have the hint to pair.
          if (options.postLogoutRedirectUri) {
            url.searchParams.set('post_logout_redirect_uri', options.postLogoutRedirectUri);
          }
        }
        window.location.href = url.toString();
        return { ok: true };
      });
    }).catch(function (err) {
      // Discovery failed but local logout already complete. Caller decides
      // whether to navigate to a fallback or leave the user on the current page.
      return { ok: false, reason: (err && err.message) || 'logout_error', localLogoutComplete: true };
    });
  }

  // NOTE: the former hidden-iframe silent-auth (silentSignIn / loadAuthInIframe)
  // was removed 2026-08-18. It could never work on this store: Shopify's authorize
  // endpoint sends X-Frame-Options: DENY + CSP frame-ancestors 'none', and so does
  // our own storefront, so the prompt=none callback can never render inside an
  // iframe to relay its code. Session persistence is now handled by
  // refreshAccessToken() (a plain token-endpoint POST — no framing), and fresh
  // sign-in by the popup flow below.

  var popupInFlight = false;

  // window.open feature string that centers the login popup over the CURRENT
  // browser window (not the primary screen), accounting for multi-monitor
  // setups via screenLeft/screenTop offsets.
  function popupFeatures() {
    var baseLeft = typeof window.screenLeft === 'number' ? window.screenLeft
      : (typeof window.screenX === 'number' ? window.screenX : 0);
    var baseTop = typeof window.screenTop === 'number' ? window.screenTop
      : (typeof window.screenY === 'number' ? window.screenY : 0);
    var viewW = window.innerWidth || (document.documentElement && document.documentElement.clientWidth) || 1024;
    var viewH = window.innerHeight || (document.documentElement && document.documentElement.clientHeight) || 768;
    var left = Math.max(0, Math.round(baseLeft + (viewW - POPUP_WIDTH) / 2));
    var top = Math.max(0, Math.round(baseTop + (viewH - POPUP_HEIGHT) / 2));
    return 'width=' + POPUP_WIDTH + ',height=' + POPUP_HEIGHT + ',left=' + left + ',top=' + top +
      ',menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes';
  }

  // Reject with new Error(reason) if `promise` does not settle within `ms`.
  function withTimeout(promise, ms, reason) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () { reject(new Error(reason)); }, ms);
      promise.then(
        function (v) { clearTimeout(t); resolve(v); },
        function (e) { clearTimeout(t); reject(e); }
      );
    });
  }

  // Interactive popup-window variant of startPkceFlow.
  //
  // Instead of navigating the whole storefront to Shopify's hosted login
  // (frame-ancestors 'none' means we can never iframe it), we open a top-level
  // popup — a browsing context that CSP framing rules do not restrict — let the
  // shopper log in there, and receive the auth code back via postMessage from
  // the popup's callback page (hpq-silent-auth-init.js relays to window.opener).
  // The opener owns the token exchange; the popup only relays. The auth code is
  // single-use, so exactly one context (this one) must call handlePkceCallback.
  //
  // Returns { ok:true, tokens } on success, or { ok:false, reason } for
  // 'popup_blocked' (caller should fall back to startPkceFlow redirect),
  // 'popup_closed', 'popup_timeout', or an OAuth error string.
  //
  // MUST be called synchronously inside a user gesture (click) — window.open is
  // invoked before any async work so popup blockers see a genuine gesture.
  function startPkcePopupFlow(opts) {
    var options = opts || {};
    if (popupInFlight) return Promise.resolve({ ok: false, reason: 'already_in_flight' });

    // Open synchronously to about:blank inside the gesture; navigate to the real
    // authorize URL once discovery + the code challenge resolve.
    var popup = null;
    try {
      popup = window.open('about:blank', POPUP_WINDOW_NAME, popupFeatures());
    } catch (e) { popup = null; }
    if (!popup) return Promise.resolve({ ok: false, reason: 'popup_blocked' });

    popupInFlight = true;
    // Mark a popup login as active so the callback page (loaded from Shopify's
    // redirect back to our origin) can identify itself as our popup and relay
    // the code, rather than exchange it itself. Cleared on every terminal path.
    storageSet(window.localStorage, POPUP_ACTIVE_KEY, '1');
    return Promise.resolve().then(function () {
      if (!options.shopDomain) throw new Error('missing_shop_domain');
      if (!options.clientId) throw new Error('missing_client_id');
      if (!options.scope) throw new Error('missing_scope');
      if (!options.redirectUri) throw new Error('missing_redirect_uri');
      requireCryptoSubtle();

      var codeVerifier = randomBase64Url(64);
      var state = randomBase64Url(32);
      storageSet(window.localStorage, PKCE_STATE_KEY, JSON.stringify({
        state: state,
        code_verifier: codeVerifier,
        created_at: Date.now()
      }));
      schedulePkceCleanup(state);

      // Bound the pre-navigation phase (discovery network + SHA-256 challenge).
      // Without this, a stalled discovery fetch would leave the blank popup open
      // and popupInFlight stuck true forever, so every retry returns
      // 'already_in_flight' and the shopper is stranded on the interstitial.
      var prep = Promise.all([
        discoverEndpoints(options.shopDomain),
        sha256Base64Url(codeVerifier)
      ]);
      return withTimeout(prep, 15000, 'discovery_timeout').then(function (parts) {
        var url = new URL(parts[0].authorization_endpoint);
        url.searchParams.set('scope', options.scope);
        url.searchParams.set('client_id', options.clientId);
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('redirect_uri', options.redirectUri);
        url.searchParams.set('state', state);
        url.searchParams.set('nonce', randomBase64Url(32));
        url.searchParams.set('code_challenge', parts[1]);
        url.searchParams.set('code_challenge_method', 'S256');
        // NO prompt=none — we WANT Shopify to render the interactive login.
        return awaitPopupResult(popup, url.toString());
      }).then(function (cb) {
        if (cb.error || cb.reason) {
          clearStoredPkceState();
          return { ok: false, reason: cb.error || cb.reason };
        }
        if (!cb.code || !cb.state) {
          clearStoredPkceState();
          return { ok: false, reason: 'popup_callback_invalid' };
        }
        return handlePkceCallback({
          shopDomain: options.shopDomain,
          clientId: options.clientId,
          redirectUri: options.redirectUri,
          code: cb.code,
          state: cb.state
        }).then(function (tokens) {
          return { ok: true, tokens: tokens };
        });
      });
    }).then(function (result) {
      popupInFlight = false;
      storageRemove(window.localStorage, POPUP_ACTIVE_KEY);
      return result;
    }, function (err) {
      popupInFlight = false;
      storageRemove(window.localStorage, POPUP_ACTIVE_KEY);
      try { if (popup && !popup.closed) popup.close(); } catch (e) {}
      clearStoredPkceState();
      return { ok: false, reason: (err && err.message) || 'popup_error' };
    });
  }

  // Navigate the popup to the authorize URL and resolve with the OAuth callback
  // params once the popup's callback page posts them back via the dedicated
  // 'hpq_popup_auth_result' message. Resolves with { reason:'popup_closed' } if
  // the shopper closes the window first, or { reason:'popup_timeout' } after 3
  // minutes.
  function awaitPopupResult(popup, authUrl) {
    return new Promise(function (resolve) {
      var settled = false;
      var closeTimer = null;
      var timeout = null;

      function settle(result) {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        if (closeTimer) { clearInterval(closeTimer); closeTimer = null; }
        if (timeout) clearTimeout(timeout);
        try { if (popup && !popup.closed) popup.close(); } catch (e) {}
        resolve(result);
      }

      function onMessage(e) {
        if (e.origin !== window.location.origin) return;
        // Bind the result channel to the popup we actually opened: reject any
        // same-origin frame/window trying to settle this flow with a forged
        // code. (e.source may be null once the popup has self-closed — only
        // reject a source that is present AND is not our popup.)
        if (e.source && popup && e.source !== popup) return;
        if (!e.data || e.data.type !== 'hpq_popup_auth_result') return;
        settle({ code: e.data.code, state: e.data.state, error: e.data.error });
      }

      window.addEventListener('message', onMessage);

      // Overall ceiling — the shopper types an email + OTP, so allow real time.
      timeout = setTimeout(function () { settle({ reason: 'popup_timeout' }); }, 180000);

      // Detect a shopper who closes the popup without finishing. A success
      // message may still be queued when the callback self-closes, so on a
      // detected close give it a short grace period for onMessage to win.
      closeTimer = setInterval(function () {
        var closed = false;
        try { closed = !!(popup && popup.closed); } catch (e) { closed = false; }
        if (closed) {
          clearInterval(closeTimer);
          closeTimer = null;
          setTimeout(function () { settle({ reason: 'popup_closed' }); }, 400);
        }
      }, 500);

      try {
        popup.location.href = authUrl;
      } catch (e) {
        settle({ error: 'popup_navigation_failed' });
      }
    });
  }

  // bfcache restore guard. When a PKCE redirect sets window.location.href and
  // the user hits browser back, Chrome restores the page from bfcache without
  // re-executing scripts — so pkceInFlight (and any stale PKCE state) is
  // preserved. Without this, every subsequent /account click silently
  // short-circuits with "another flow already in progress".
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    pkceInFlight = false;
    popupInFlight = false;
    storageRemove(window.localStorage, POPUP_ACTIVE_KEY);
    clearStoredPkceState();
  });

  window.HPQCustomerAccountAPI = {
    // Exposed so hpq-flyout-auth-state.js (and any other load-order
    // fallback) reads the SAME expiry skew this module uses. A literal
    // duplicate in the flyout file is the comment-locally-valid-but-
    // globally-stale pattern this constant replaces.
    EXPIRY_SKEW_MS: EXPIRY_SKEW_MS,
    discoverEndpoints: discoverEndpoints,
    generateIdempotencyNonce: generateIdempotencyNonce,
    buildCanonicalRedirectUri: buildCanonicalRedirectUri,
    buildPopupRedirectUri: buildPopupRedirectUri,
    isPopupLoginSupported: isPopupLoginSupported,
    startPkceFlow: startPkceFlow,
    startPkcePopupFlow: startPkcePopupFlow,
    refreshAccessToken: refreshAccessToken,
    getStoredRefreshToken: getStoredRefreshToken,
    handlePkceCallback: handlePkceCallback,
    handlePkceCallbackError: handlePkceCallbackError,
    getCustomerFromTokens: getCustomerFromTokens,
    getCachedTokens: getCachedTokens,
    getCachedCustomer: getCachedCustomer,
    hasUsableSession: hasUsableSession,
    clearTokens: clearTokens,
    clearSession: clearSession,
    readAdoptableTokens: readAdoptableTokens,
    logout: logout
  };
})();
